import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  Alert,
  Descriptions,
  Statistic,
  Divider,
  message,
  Popconfirm,
  Timeline,
  Badge
} from 'antd';
import {
  CreditCardOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  BankOutlined,
  ShoppingOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Plan {
  name: string;
  price: number;
  duration: number;
  features: string[];
  maxStations: number;
  maxCampaigns: number;
  maxSubUsers: number;
  analyticsEnabled: boolean;
  apiAccess: boolean;
  prioritySupport: boolean;
}

interface Subscription {
  _id: string;
  plan: string;
  status: string;
  startDate: string;
  endDate: string;
  autoRenew: boolean;
  nextBillingDate?: string;
  planDetails: Plan;
}

interface Payment {
  _id: string;
  amount: number;
  currency: string;
  plan: string;
  status: string;
  paymentMethod: string;
  transactionId?: string;
  paymentDate?: string;
  expiryDate: string;
  createdAt: string;
}

const PaymentSubscription: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Record<string, Plan>>({});
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [paymentDetailModalVisible, setPaymentDetailModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [form] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchSubscription();
    fetchPayments();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await stationOwnerAPI.getPlanDetails();
      if (response.success) {
        setPlans(response.plans);
      }
    } catch (error: any) {
      message.error('Plan bilgileri yüklenemedi');
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await stationOwnerAPI.getSubscription();
      if (response.success) {
        setSubscription(response.subscription);
      }
    } catch (error: any) {
      message.error('Abonelik bilgisi yüklenemedi');
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.getPaymentHistory();
      if (response.success) {
        setPayments(response.payments || []);
      }
    } catch (error: any) {
      message.error('Ödeme geçmişi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (values: any) => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.createPayment({
        plan: values.plan,
        duration: values.duration,
        paymentMethod: values.paymentMethod,
        paymentData: {
          cardNumber: values.cardNumber,
          cardHolderName: values.cardHolderName,
          expireMonth: values.expireMonth,
          expireYear: values.expireYear,
          cvc: values.cvc,
          cardBrand: values.cardBrand
        }
      });

      if (response.success) {
        if (response.redirectUrl) {
          // PayTR için redirect
          window.location.href = response.redirectUrl;
        } else {
          message.success('Ödeme başarıyla tamamlandı');
          setPaymentModalVisible(false);
          form.resetFields();
          fetchSubscription();
          fetchPayments();
        }
      } else {
        message.error(response.message || 'Ödeme işlemi başarısız');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ödeme işlemi başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (values: any) => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.cancelSubscription({
        reason: values.reason
      });

      if (response.success) {
        message.success('Abonelik iptal edildi');
        setCancelModalVisible(false);
        cancelForm.resetFields();
        fetchSubscription();
      }
    } catch (error: any) {
      message.error('Abonelik iptal edilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPaymentDetail = async (paymentId: string) => {
    try {
      const response = await stationOwnerAPI.getPaymentDetail(paymentId);
      if (response.success) {
        setSelectedPayment(response.payment);
        setPaymentDetailModalVisible(true);
      }
    } catch (error: any) {
      message.error('Ödeme detayı yüklenemedi');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'green',
      inactive: 'default',
      cancelled: 'red',
      expired: 'orange',
      suspended: 'red',
      pending: 'orange',
      processing: 'blue',
      completed: 'green',
      failed: 'red',
      refunded: 'purple'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      active: 'Aktif',
      inactive: 'Pasif',
      cancelled: 'İptal Edildi',
      expired: 'Süresi Doldu',
      suspended: 'Askıya Alındı',
      pending: 'Beklemede',
      processing: 'İşleniyor',
      completed: 'Tamamlandı',
      failed: 'Başarısız',
      refunded: 'İade Edildi'
    };
    return texts[status] || status;
  };

  const paymentColumns = [
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
      responsive: ['md'] as any
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan: string) => plan.toUpperCase(),
      responsive: ['md'] as any
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: Payment) => `${amount} ${record.currency}`,
      responsive: ['md'] as any
    },
    {
      title: 'Yöntem',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: string) => {
        const methods: Record<string, string> = {
          credit_card: 'Kredi Kartı',
          bank_transfer: 'Banka Havalesi',
          paypal: 'PayPal',
          stripe: 'Stripe',
          iyzico: 'iyzico',
          paytr: 'PayTR'
        };
        return methods[method] || method;
      },
      responsive: ['md'] as any
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Payment) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleViewPaymentDetail(record._id)}
          >
            Detay
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      <Title level={2} style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '24px' }}>
        <DollarOutlined /> Ödeme ve Abonelik
      </Title>

      {/* Mevcut Abonelik */}
      {subscription && (
        <Card
          title={
            <Space>
              <ShoppingOutlined />
              <span>Mevcut Abonelik</span>
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Plan"
                value={subscription.planDetails?.name || subscription.plan}
                prefix={<InfoCircleOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Durum"
                value={getStatusText(subscription.status)}
                prefix={
                  <Badge
                    status={subscription.status === 'active' ? 'success' : 'error'}
                  />
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Bitiş Tarihi"
                value={dayjs(subscription.endDate).format('DD.MM.YYYY')}
                prefix={<CalendarOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Otomatik Yenileme"
                value={subscription.autoRenew ? 'Açık' : 'Kapalı'}
                prefix={<ReloadOutlined />}
              />
            </Col>
          </Row>

          {subscription.status === 'active' && (
            <>
              <Divider />
              <Space>
                <Button
                  type="primary"
                  icon={<CreditCardOutlined />}
                  onClick={() => setPaymentModalVisible(true)}
                >
                  Plan Yükselt
                </Button>
                <Popconfirm
                  title="Aboneliği iptal etmek istediğinizden emin misiniz?"
                  onConfirm={() => setCancelModalVisible(true)}
                  okText="Evet"
                  cancelText="Hayır"
                >
                  <Button danger>Aboneliği İptal Et</Button>
                </Popconfirm>
              </Space>
            </>
          )}
        </Card>
      )}

      {/* Planlar */}
      <Card
        title={
          <Space>
            <InfoCircleOutlined />
            <span>Mevcut Planlar</span>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[16, 16]}>
          {Object.entries(plans).map(([key, plan]) => (
            <Col xs={24} sm={12} md={6} key={key}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  border: subscription?.plan === key ? '2px solid #1890ff' : undefined
                }}
                actions={[
                  <Button
                    type={subscription?.plan === key ? 'default' : 'primary'}
                    block
                    onClick={() => {
                      form.setFieldsValue({ plan: key, duration: 1 });
                      setPaymentModalVisible(true);
                    }}
                    disabled={subscription?.plan === key}
                  >
                    {subscription?.plan === key ? 'Mevcut Plan' : 'Seç'}
                  </Button>
                ]}
              >
                <Title level={4}>{plan.name}</Title>
                <Title level={2} style={{ color: '#1890ff' }}>
                  {plan.price === 0 ? 'Ücretsiz' : `${plan.price}₺/ay`}
                </Title>
                <ul style={{ paddingLeft: '20px', marginTop: '16px' }}>
                  {plan.features.map((feature, index) => (
                    <li key={index} style={{ marginBottom: '8px' }}>
                      {feature}
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Ödeme Geçmişi */}
      <Card title="Ödeme Geçmişi">
        <Table
          columns={paymentColumns}
          dataSource={payments}
          loading={loading}
          rowKey="_id"
          pagination={{
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: true }}
        />
      </Card>

      {/* Ödeme Modal */}
      <Modal
        title="Ödeme Yap"
        open={paymentModalVisible}
        onCancel={() => {
          setPaymentModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePayment}
          initialValues={{ duration: 1 }}
        >
          <Form.Item
            name="plan"
            label="Plan"
            rules={[{ required: true, message: 'Plan seçiniz' }]}
          >
            <Select>
              {Object.entries(plans).map(([key, plan]) => (
                <Option key={key} value={key}>
                  {plan.name} - {plan.price === 0 ? 'Ücretsiz' : `${plan.price}₺/ay`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="duration"
            label="Süre (Ay)"
            rules={[{ required: true, message: 'Süre seçiniz' }]}
          >
            <Select>
              <Option value={1}>1 Ay</Option>
              <Option value={3}>3 Ay (%5 İndirim)</Option>
              <Option value={6}>6 Ay (%10 İndirim)</Option>
              <Option value={12}>12 Ay (%15 İndirim)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="paymentMethod"
            label="Ödeme Yöntemi"
            rules={[{ required: true, message: 'Ödeme yöntemi seçiniz' }]}
          >
            <Select>
              <Option value="credit_card">Kredi Kartı (iyzico)</Option>
              <Option value="paytr">PayTR</Option>
              <Option value="stripe">Stripe</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="cardNumber"
            label="Kart Numarası"
            rules={[{ required: true, message: 'Kart numarası giriniz' }]}
          >
            <Input placeholder="1234 5678 9012 3456" maxLength={19} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="expireMonth"
                label="Ay"
                rules={[{ required: true, message: 'Ay seçiniz' }]}
              >
                <Select placeholder="Ay">
                  {Array.from({ length: 12 }, (_, i) => (
                    <Option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {String(i + 1).padStart(2, '0')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expireYear"
                label="Yıl"
                rules={[{ required: true, message: 'Yıl seçiniz' }]}
              >
                <Select placeholder="Yıl">
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <Option key={year} value={String(year)}>
                        {year}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cvc"
                label="CVC"
                rules={[{ required: true, message: 'CVC giriniz' }]}
              >
                <Input placeholder="123" maxLength={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cardHolderName"
                label="Kart Sahibi"
                rules={[{ required: true, message: 'Kart sahibi adı giriniz' }]}
              >
                <Input placeholder="Ad Soyad" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Ödeme Yap
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* İptal Modal */}
      <Modal
        title="Abonelik İptal"
        open={cancelModalVisible}
        onCancel={() => {
          setCancelModalVisible(false);
          cancelForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '90%' : 500}
      >
        <Alert
          message="Dikkat"
          description="Aboneliğinizi iptal ettiğinizde, mevcut planınızın süresi dolana kadar kullanmaya devam edebilirsiniz. Süre dolduktan sonra ücretsiz plana geçeceksiniz."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form form={cancelForm} layout="vertical" onFinish={handleCancelSubscription}>
          <Form.Item
            name="reason"
            label="İptal Nedeni"
            rules={[{ required: true, message: 'İptal nedeni giriniz' }]}
          >
            <Input.TextArea rows={4} placeholder="İptal nedeninizi belirtiniz..." />
          </Form.Item>
          <Form.Item>
            <Button type="primary" danger htmlType="submit" block loading={loading}>
              Aboneliği İptal Et
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Ödeme Detay Modal */}
      <Modal
        title="Ödeme Detayı"
        open={paymentDetailModalVisible}
        onCancel={() => {
          setPaymentDetailModalVisible(false);
          setSelectedPayment(null);
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        {selectedPayment && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Tutar">
              {selectedPayment.amount} {selectedPayment.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              {selectedPayment.plan.toUpperCase()}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={getStatusColor(selectedPayment.status)}>
                {getStatusText(selectedPayment.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Ödeme Yöntemi">
              {selectedPayment.paymentMethod}
            </Descriptions.Item>
            {selectedPayment.transactionId && (
              <Descriptions.Item label="İşlem ID">
                {selectedPayment.transactionId}
              </Descriptions.Item>
            )}
            {selectedPayment.paymentDate && (
              <Descriptions.Item label="Ödeme Tarihi">
                {dayjs(selectedPayment.paymentDate).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Oluşturulma Tarihi">
              {dayjs(selectedPayment.createdAt).format('DD.MM.YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default PaymentSubscription;

